import React, { use, useCallback, useMemo } from 'react';

import { ConfigProvider } from 'antd';
import { match } from 'ts-pattern';

import Constant from '@core/app/actions/beambox/constant';
import { iconButtonTheme } from '@core/app/constants/antd-config';
import { useIsMobile } from '@core/app/stores/screenStore';
import HistoryCommandFactory from '@core/app/svgedit/history/HistoryCommandFactory';
import undoManager from '@core/app/svgedit/history/undoManager';
import { resizeSelector } from '@core/app/svgedit/selector';
import { setFitTextBBox } from '@core/app/svgedit/text/fitText';
import { isFitText } from '@core/app/svgedit/text/textedit';
import { getIsVertical } from '@core/app/svgedit/text/textedit/getters';
import { setRotationAngle } from '@core/app/svgedit/transform/rotation';
import { getSVGAsync } from '@core/helpers/svg-editor-helper';
import SymbolMaker from '@core/helpers/symbol-helper/symbolMaker';
import useForceUpdate from '@core/helpers/use-force-update';
import type { ICommand } from '@core/interfaces/IHistory';
import type ISVGCanvas from '@core/interfaces/ISVGCanvas';
import type { DimensionOrderMap, DimensionValues, SizeKey } from '@core/interfaces/ObjectPanel';
import { isPositionKey, isSizeKeyShort } from '@core/interfaces/ObjectPanel';

import { ObjectPanelContext } from '../contexts/ObjectPanelContext';
import ObjectPanelItem from '../ObjectPanelItem';

import styles from './DimensionPanel.module.scss';
import FlipButtons from './FlipButtons';
import PositionInput from './PositionInput';
import RatioLock from './RatioLock';
import Rotation from './Rotation';
import SizeInput from './SizeInput';
import { getValue } from './utils';

let svgCanvas: ISVGCanvas;

getSVGAsync((globalSVG) => {
  svgCanvas = globalSVG.Canvas;
});

const panelMap: DimensionOrderMap = {
  ellipse: ['cx', 'cy', 'rx', 'ry'],
  g: ['x', 'y', 'w', 'h'],
  image: ['x', 'y', 'w', 'h'],
  img: ['x', 'y', 'w', 'h'],
  line: ['x1', 'y1', 'x2', 'y2'],
  path: ['x', 'y', 'w', 'h'],
  polygon: ['x', 'y', 'w', 'h'],
  rect: ['x', 'y', 'w', 'h'],
  text: ['x', 'y', 'w', 'h'],
  use: ['x', 'y', 'w', 'h'],
};

const panelMapMobile: DimensionOrderMap = {
  ellipse: ['rx', 'lock', 'ry', 'rot', 'cx', 'cy'],
  g: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  image: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  img: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  line: ['x1', 'y1', 'lock', 'x2', 'y2', 'rot'],
  path: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  polygon: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  rect: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  text: ['w', 'lock', 'h', 'rot', 'x', 'y'],
  use: ['w', 'lock', 'h', 'rot', 'x', 'y'],
};

const fixedSizeMapping: { [key in SizeKey]: SizeKey } = {
  height: 'width',
  rx: 'ry',
  ry: 'rx',
  width: 'height',
};

interface Props {
  elem: null | SVGElement;
}

const DimensionPanel = ({ elem }: Props): React.JSX.Element => {
  const { getDimensionValues, updateDimensionValues } = use(ObjectPanelContext);

  const isMobile = useIsMobile();

  const forceUpdate = useForceUpdate();

  const handleSizeBlur = useCallback(async () => {
    if (elem?.tagName === 'use') {
      SymbolMaker.reRenderImageSymbol(elem as SVGUseElement);
    } else if (elem?.tagName === 'g') {
      const allUses = Array.from(elem.querySelectorAll('use'));

      SymbolMaker.reRenderImageSymbolArray(allUses);
    }
  }, [elem]);
  const isFitTextElem = useMemo(() => {
    if (!elem) return false;

    return isFitText(elem);
  }, [elem]);

  const getDisabledSizeKeys = useCallback((): Set<'h' | 'w'> => {
    const disabled = new Set<'h' | 'w'>();

    if (!elem) return disabled;

    const fitTextElems = [elem, ...elem.querySelectorAll('text')].filter((e) => isFitText(e)) as SVGTextElement[];

    for (const text of fitTextElems) {
      if (disabled.has('h') && disabled.has('w')) {
        break;
      }

      disabled.add(getIsVertical(text) ? 'w' : 'h');
    }

    return disabled;
  }, [elem]);

  const handlePositionChange = useCallback(
    (type: string, val: number): void => {
      if (!elem) return;

      const posVal = val * Constant.dpmm;

      if (!['text', 'use'].includes(elem.tagName)) {
        svgCanvas.changeSelectedAttribute(type, posVal, [elem]);
      } else {
        svgCanvas.setSvgElemPosition(type as 'x' | 'y', posVal, elem);
      }

      updateDimensionValues({ [type]: posVal });
      forceUpdate();
    },
    [elem, updateDimensionValues, forceUpdate],
  );

  const handleRotationChange = useCallback(
    (val: number, addToHistory = false): void => {
      if (!elem) return;

      const isTempGroup = elem.getAttribute('data-tempgroup') === 'true';
      let rotationDeg = val % 360;

      if (rotationDeg > 180) {
        rotationDeg -= 360;
      }

      let finalRotation = rotationDeg;

      if (isTempGroup && !addToHistory) {
        setRotationAngle(elem, rotationDeg, { addToHistory: false });
      } else {
        setRotationAngle(elem, rotationDeg, { addToHistory });

        if (isTempGroup) finalRotation = 0;
      }

      updateDimensionValues({ rotation: finalRotation });
      resizeSelector(elem);

      forceUpdate();
    },
    [elem, forceUpdate, updateDimensionValues],
  );

  const changeSize = useCallback(
    (type: SizeKey, val: number): ICommand | null => {
      const elemSize = val > 0.1 ? val : 0.1;
      let cmd: ICommand | null = null;

      match(elem?.tagName)
        .with('ellipse', 'rect', 'image', () => {
          undoManager.beginUndoableChange(type, [elem!]);
          svgCanvas.changeSelectedAttributeNoUndo(type, elemSize, [elem!]);

          const batchCmd = undoManager.finishUndoableChange();

          cmd = batchCmd.isEmpty() ? null : batchCmd;
        })
        .with('g', 'polygon', 'path', 'text', 'use', (tag) => {
          if (tag === 'text' && isFitText(elem!)) {
            cmd = setFitTextBBox(elem as SVGTextElement, { [type]: elemSize }, { addToHistory: false });
          } else {
            const batchCmd = svgCanvas.setSvgElemSize(type, elemSize);

            cmd = batchCmd?.isEmpty() ? null : batchCmd;
          }
        })
        .otherwise(() => {});

      return cmd;
    },
    [elem],
  );

  const handleSizeChange = useCallback(
    (type: SizeKey, val: number): void => {
      const batchCmd = HistoryCommandFactory.createBatchCommand('Object Panel Size Change');
      const response = {
        dimensionValues: {} as DimensionValues,
      };

      getDimensionValues(response);

      const { dimensionValues } = response;
      const isRatioFixed = dimensionValues.isRatioFixed || false;
      const sizeVal = val * Constant.dpmm;

      let cmd = changeSize(type, sizeVal);

      if (cmd) {
        batchCmd.addSubCommand(cmd);
      }

      const newValues = { [type]: sizeVal };

      if (isRatioFixed) {
        const ratio = sizeVal / dimensionValues[type]!;
        const counterPart = fixedSizeMapping[type];
        const newCounterPartVal = ratio * dimensionValues[counterPart]!;

        cmd = changeSize(counterPart, newCounterPartVal);

        if (cmd) {
          batchCmd.addSubCommand(cmd);
        }

        newValues[counterPart] = newCounterPartVal;
      }

      updateDimensionValues(newValues);

      if (batchCmd && !batchCmd.isEmpty()) {
        undoManager.addCommandToHistory(batchCmd);
      }

      forceUpdate();
    },
    [changeSize, getDimensionValues, updateDimensionValues, forceUpdate],
  );

  const handleFixRatio = useCallback((): void => {
    const isRatioFixed = elem?.getAttribute('data-ratiofixed') === 'true';

    if (elem) {
      svgCanvas.changeSelectedAttribute('data-ratiofixed', String(!isRatioFixed), [elem]);
    }

    updateDimensionValues({ isRatioFixed: !isRatioFixed });
    forceUpdate();
  }, [elem, updateDimensionValues, forceUpdate]);

  const response = { dimensionValues: {} as DimensionValues };

  getDimensionValues(response);

  const { dimensionValues } = response;
  const disabledSizeKeys = getDisabledSizeKeys();

  const renderBlock = (type: string): React.ReactNode => {
    if (isPositionKey(type)) {
      return (
        <PositionInput
          key={type}
          onChange={handlePositionChange}
          type={type}
          value={getValue(dimensionValues, type, { unit: 'mm' })}
        />
      );
    }

    if (isSizeKeyShort(type)) {
      return (
        <SizeInput
          disabled={disabledSizeKeys.has(type as 'h' | 'w')}
          key={type}
          onBlur={handleSizeBlur}
          onChange={handleSizeChange}
          type={type}
          value={getValue(dimensionValues, type, { unit: 'mm' })}
        />
      );
    }

    if (type === 'rot') {
      return <Rotation key="rot" onChange={handleRotationChange} value={dimensionValues.rotation || 0} />;
    }

    if (type === 'lock' && !isFitTextElem) {
      return <RatioLock isLocked={dimensionValues.isRatioFixed || false} key="lock" onClick={handleFixRatio} />;
    }

    return null;
  };
  const tagName = elem?.tagName.toLowerCase() ?? '';
  const panels: string[] = (isMobile ? panelMapMobile : panelMap)[tagName] || ['x', 'y', 'w', 'h'];
  const contents: React.ReactNode[] = [];

  panels.forEach((type) => {
    contents.push(renderBlock(type));
  });

  return isMobile ? (
    <div className={styles.container}>
      <ObjectPanelItem.Divider />
      {contents}
      <FlipButtons />
    </div>
  ) : (
    <div className={styles.panel}>
      <ConfigProvider theme={iconButtonTheme}>
        <div className={styles.row}>
          <div className={styles.dimensions}>{contents}</div>
          {renderBlock('lock')}
        </div>
        <div className={styles.row}>
          {renderBlock('rot')}
          <FlipButtons />
        </div>
      </ConfigProvider>
    </div>
  );
};

export default DimensionPanel;
